import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { useSEO, BASE } from "@/lib/seo";
import { METIERS } from "@/lib/partenaires";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";
import NotFound from "@/pages/NotFound";

const HYDRO_IMAGES = [
  { src: "/images/hydrographie-peinture-decor-1.webp", alt: "Objet décoré par hydrographie peinture, Hydrographie Concept" },
  { src: "/images/hydrographie-peinture-decor-2.webp", alt: "Impression hydrographique sur support, Hydrographie Concept" },
  { src: "/images/hydrographie-peinture-decor-3.webp", alt: "Décor hydrographique réalisé par Hydrographie Concept" },
];
const FONDERIE_IMAGES = [
  { src: "/images/fdp-personnage.webp", alt: "sculpture bronze fonderie du Péroux Vonnas" },
  { src: "/images/fdp-plante.webp", alt: "sculpture bronze fonderie du Péroux à Vonnas" },
];

const FICHES = {
  "hydrographie-concept": {
    metier: "finitions",
    title: "Hydrographie peinture et impression hydrographique | Sculpteur Français",
    description:
      "Hydrographie peinture et impression hydrographique sur tous supports : Hydrographie Concept, à Saint-Just-Saint-Rambert, propose environ 6 000 motifs, sur mesure.",
    headline: "Hydrographie Concept, hydrographie peinture et impression hydrographique",
    titleLines: ["Hydrographie Concept,", "hydrographie peinture et", "impression hydrographique"],
    lead: "Hydrographie Concept est spécialisée dans l'hydrographie, que l'on appelle aussi impression hydrographique ou hydrodipping. Le principe : un film imprimé est déposé à la surface de l'eau, l'objet y est plongé, et le motif se transfère en épousant chaque courbe. L'atelier est installé à Saint-Just-Saint-Rambert, dans la Loire.",
    image: {
      src: "/images/hydrographie-concept-logo.webp",
      alt: "Logo Hydrographie Concept, impression hydrographique sur tout support",
      caption: "Hydrographie Concept — impression hydrographique sur tout support",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Finitions et décors de surface",
    organization: {
      "@type": "Organization",
      name: "Hydrographie Concept",
      url: "https://www.hydrographie-concept.fr",
      address: { "@type": "PostalAddress", addressLocality: "Saint-Just-Saint-Rambert", addressRegion: "Loire", addressCountry: "FR" },
    },
    ogTitle: "Hydrographie peinture et impression hydrographique",
    sections: [
      {
        h2: "Un atelier d'hydrographie pour tous les supports",
        paragraphs: [
          "Hydrographie Concept dispose d'environ 6 000 motifs et intervient sur tout type de support. L'atelier personnalise des objets et des surfaces, réalise des projets uniques et sur mesure, et redonne vie à des objets anciens par l'upcycling.",
          "Il accompagne aussi bien les particuliers que les professionnels, en France comme à l'international.",
        ],
      },
      {
        h2: "L'hydrographie au service de la sculpture",
        paragraphs: [
          "Pour un sculpteur, l'hydrographie ouvre une piste rare : habiller un volume d'un décor continu, qui suit le relief sans raccord visible, là où le pinceau trouve vite ses limites.",
        ],
      },
    ],
    contactH2: "Contacter Hydrographie Concept",
    city: "Saint-Just-Saint-Rambert (Loire)",
    siteUrl: "https://www.hydrographie-concept.fr",
    siteLabel: "www.hydrographie-concept.fr",
    images: HYDRO_IMAGES,
  },
  "fonderie-du-peroux": {
    metier: "fonderie-art",
    title: "Fonderie d’art, bronze, moules, coulage, patine, coulage | Sculpteur Français",
    description: "Fonderie d’art, bronze, moules, coulage, patine, coulage: Fonderie d’art, à Vonnas.",
    headline: "Fonderie d’art, bronze, moules, coulage, patine, coulage: Fonderie d’art, à Vonnas.",
    titleLines: ["Fonderie d’art,", "bronze, moules, coulage,", "patine, coulage : Fonderie d’art,", "à Vonnas"],
    lead: "La Fonderie du Péroux est un atelier spécialisé dans la fonderie d’art à la cire perdue et au sable. Elle travaille différents métaux, notamment le bronze, le laiton et l’aluminium, pour donner forme aux projets des artistes et des sculpteurs.",
    image: {
      src: "/images/fdp-vague.webp",
      alt: "sculpture bronze fonderie du Péroux",
      caption: "Fonderie du Péroux — sculpture en bronze, fonderie d’art à Vonnas",
      className: "mx-auto w-auto max-w-md",
    },
    breadcrumbMetier: "Fondeur d’art",
    organization: {
      "@type": "Organization",
      name: "Fonderie du Péroux",
      address: { "@type": "PostalAddress", addressLocality: "Vonnas", postalCode: "01540", addressCountry: "FR" },
    },
    ogTitle: "Fonderie d’art, bronze, moules, coulage, patine, coulage",
    sections: [
      {
        h2: "Un atelier de fonderie d’art à Vonnas",
        paragraphs: [
          "La Fonderie du Péroux est un atelier spécialisé dans la fonderie d’art à la cire perdue et au sable. Elle travaille différents métaux, notamment le bronze, le laiton et l’aluminium, pour donner forme aux projets des artistes et des sculpteurs.",
          "De la conception à la réalisation, l’atelier met son savoir-faire technique au service de la création artistique, de la fabrication de pièces originales et de la reproduction de sculptures.",
        ],
      },
      {
        h2: "La fonderie d’art au service de la sculpture",
        paragraphs: [
          "La fonderie d’art occupe une place essentielle dans la réalisation d’une sculpture. Elle permet de transposer une œuvre originale dans le métal, tout en préservant ses volumes, ses détails, ses textures et l'intention artistique du sculpteur.",
          "Grâce aux techniques de fonte à la cire perdue et au sable, la Fonderie du Péroux accompagne les artistes dans la réalisation de sculptures en bronze, en laiton ou en aluminium, depuis la préparation du modèle jusqu'à l'obtention de la pièce fondue.",
        ],
      },
      {
        h2: "Stages et formations aux techniques de fonderie d’art",
        paragraphs: [
          "La transmission des savoir-faire fait également partie des activités de l'atelier. La Fonderie du Péroux propose des stages et des formations permettant de découvrir les différentes étapes de la fonderie d’art, de comprendre les techniques de moulage et de fonte et de se familiariser avec le travail du métal.",
          "Ces formations s'adressent aux artistes, aux sculpteurs et aux personnes désireuses de découvrir les pratiques traditionnelles de la fonderie.",
        ],
      },
      {
        h2: "Conception et réalisation d’ateliers de fonderie d’art",
        paragraphs: [
          "Au-delà de la production de sculptures, l’atelier apporte son expertise dans la conception et la réalisation de projets d’ateliers de fonderie d’art.",
          "Il intervient dans l'étude des besoins techniques, le choix des équipements et l'organisation des installations nécessaires à la pratique de la fonte artistique.",
        ],
      },
    ],
    contactH2: "Contacter la Fonderie du Péroux",
    cityLabel: "Adresse",
    city: "2075 Rte de Péroux, 01290 Biziat",
    address: "",
    tel: "06 32 79 71 56",
    telHref: "+33632797156",
    email: "fonderieduperoux@gmail.com",
    siteUrl: "",
    siteLabel: "",
    images: FONDERIE_IMAGES,
  },
};

export default function PartenaireDetailPage() {
  const { metier: metierSlug, partner: partnerId } = useParams();
  const metier = METIERS.find((m) => m.slug === metierSlug);
  const partner = metier?.partners.find((p) => p.id === partnerId && p.detailPage);
  const fiche = FICHES[partnerId];

  useSEO(
    fiche ? fiche.title : "Partenaire — Sculpteur Français",
    fiche ? fiche.description : "Fiche partenaire de l'annuaire des métiers d'art.",
    fiche
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: fiche.headline,
          inLanguage: "fr-FR",
          mainEntityOfPage: `${BASE}/partenaires/${fiche.metier}/${partnerId}`,
          about: fiche.organization,
          author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
          publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
        }
      : null,
    fiche
      ? {
          type: "article",
          ogTitle: fiche.ogTitle,
          ogDescription: fiche.description,
          breadcrumb: [
            { name: "Partenaires", path: "/partenaires-metiers-d-art" },
            { name: fiche.breadcrumbMetier, path: `/partenaires/${fiche.metier}` },
            { name: partner.name, path: `/partenaires/${fiche.metier}/${partnerId}` },
          ],
        }
      : { noindex: true }
  );

  if (!partner || !fiche) return <NotFound />;

  return (
    <ArticleShell
      testId="partenaire-fiche-page"
      eyebrow={`Partenaires — ${metier.name}`}
      titleLines={fiche.titleLines}
      lead={fiche.lead}
      image={fiche.image}
    >
      <Link
        to={`/partenaires/${fiche.metier}`}
        data-testid="fiche-back-link"
        className="group inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
      >
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
        <span className="link-underline">{fiche.breadcrumbMetier}</span>
      </Link>

      {fiche.sections.map((s) => (
        <section key={s.h2}>
          <H2>{s.h2}</H2>
          {s.paragraphs.map((t, i) => (
            <P key={i}>{t}</P>
          ))}
        </section>
      ))}

      <div className="my-10 space-y-10">
        {fiche.images.map((img, i) => (
          <Reveal key={img.src}>
            <figure>
              <div className="img-content-wrap">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="img-content border border-ink/10"
                  data-testid={`fiche-photo-${i + 1}`}
                />
              </div>
            </figure>
          </Reveal>
        ))}
      </div>

      <H2>{fiche.contactH2}</H2>
      <div className="mt-8 border border-ink/15 px-6 py-5" data-testid="fiche-contact">
        <p className="flex items-center gap-2 text-base font-light text-body sm:text-lg">
          <MapPin className="h-4 w-4 shrink-0 text-bronze" />
          {fiche.cityLabel || "Ville"} : {fiche.city}
        </p>
        {fiche.address && (
          <p className="mt-2 flex items-start gap-2 text-base font-light text-body sm:text-lg">
            <MapPin className="h-4 w-4 shrink-0 text-bronze" />
            {fiche.address}
          </p>
        )}
        {fiche.tel && (
          <p className="mt-3 flex items-center gap-2 text-base font-light text-body sm:text-lg">
            <Phone className="h-4 w-4 shrink-0 text-bronze" />
            Téléphone :{" "}
            <a href={`tel:${fiche.telHref}`} className="link-underline text-bronze transition-colors hover:text-ink">
              {fiche.tel}
            </a>
          </p>
        )}
        {fiche.email && (
          <p className="mt-3 flex items-center gap-2 text-base font-light text-body sm:text-lg">
            <Mail className="h-4 w-4 shrink-0 text-bronze" />
            E-mail :{" "}
            <a href={`mailto:${fiche.email}`} className="link-underline text-bronze transition-colors hover:text-ink">
              {fiche.email}
            </a>
          </p>
        )}
        {(fiche.siteUrl || fiche.siteLabel === "") && fiche.siteLabel !== undefined && (
          <p className="mt-3 flex items-center gap-2 text-base font-light text-body sm:text-lg">
            <ArrowUpRight className="h-4 w-4 shrink-0 text-bronze" />
            Site internet :{" "}
            {fiche.siteUrl ? (
              <a
                href={fiche.siteUrl}
                target="_blank"
                rel="noopener"
                data-testid="fiche-site-link"
                className="link-underline text-bronze transition-colors hover:text-ink"
              >
                {fiche.siteLabel}
              </a>
            ) : null}
          </p>
        )}
      </div>

      <SeeAlso
        links={[
          { to: `/partenaires/${fiche.metier}`, label: fiche.breadcrumbMetier },
          { to: "/partenaires-metiers-d-art", label: "Tous les métiers partenaires" },
          { to: "/devenez-partenaire-metiers-d-art", label: "Inscription gratuite à l'annuaire" },
        ]}
      />
    </ArticleShell>
  );
}
